import { useCallback, useEffect, useRef, useState } from 'react'

import { chatApi } from '@carrent/api'
import { useEventStream } from '@carrent/core'

/**
 * Лента диалога с реалтаймом.
 *
 * Отличие от остальной системы: chat.message несёт сообщение целиком,
 * и оно ДОБАВЛЯЕТСЯ в ленту без перезагрузки — лента чата чувствительна
 * к задержке. Безопасность приёма обеспечивают два механизма:
 *
 * 1. Дедупликация по id И по client_message_id: событие может прийти
 *    повторно при доигрывании после разрыва, а собственное сообщение —
 *    вернуться событием раньше, чем ответ POST из очереди.
 *
 * 2. Полная перезагрузка при восстановлении потока: если разрыв был
 *    дольше журнала доигрывания (Redis Stream хранит ~500 записей),
 *    события потеряны безвозвратно — при переходе потока обратно
 *    в live лента перечитывается целиком. Это принятый в системе
 *    паттерн «отсутствовал долго — перезагрузи».
 *
 * Обнаружение дыр по номерам сознательно НЕ используется:
 * id сообщений глобальны для всей таблицы, а не последовательны
 * внутри диалога, поэтому «пропуск номера» ничего не означает.
 *
 * Опрос — только когда поток не работает (правило useLiveData).
 */

const POLL_MS = 15_000
export const CHAT_PAGE_SIZE = 50

function dedupeAppend(items, incoming) {
  const byId = new Set(items.map((m) => m.id))
  const byClientId = new Set(items.map((m) => m.client_message_id))
  if (byId.has(incoming.id) || byClientId.has(incoming.client_message_id)) {
    return items
  }
  return [...items, incoming]
}

export function useConversation(conversationId, { enabled = true } = {}) {
  const [messages, setMessages] = useState([])
  const [pinned, setPinned] = useState([])
  const [details, setDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [hasMore, setHasMore] = useState(false)
  const nextBeforeRef = useRef(null)
  const pollTimer = useRef(null)
  const wasLiveRef = useRef(false)

  const loadDetails = useCallback(async () => {
    const { data } = await chatApi.conversation(conversationId)
    setDetails(data.data)
    setPinned(data.data.pinned || [])
  }, [conversationId])

  /** Полная перезагрузка последней страницы: старт и возврат из долгого обрыва. */
  const reload = useCallback(async () => {
    setError('')
    try {
      const [{ data }] = await Promise.all([
        chatApi.messages(conversationId),
        loadDetails(),
      ])
      const page = data.data
      // API отдаёт новые сверху; лента хранит хронологию.
      setMessages([...page.items].reverse())
      setHasMore(page.has_more)
      nextBeforeRef.current = page.next_before_id
    } catch (e) {
      setError(e?.response?.data?.message || 'Не удалось загрузить диалог')
    } finally {
      setLoading(false)
    }
  }, [conversationId, loadDetails])

  /** Догрузка старых сообщений (скролл вверх). */
  const loadOlder = useCallback(async () => {
    if (!hasMore || !nextBeforeRef.current) return
    const { data } = await chatApi.messages(conversationId, {
      beforeId: nextBeforeRef.current,
    })
    const page = data.data
    setMessages((cur) => [...[...page.items].reverse(), ...cur])
    setHasMore(page.has_more)
    nextBeforeRef.current = page.next_before_id
  }, [conversationId, hasMore])

  useEffect(() => {
    if (!enabled) return
    setLoading(true)
    setMessages([])
    nextBeforeRef.current = null
    reload()
  }, [enabled, reload])

  const onEvent = useCallback(
    (event) => {
      const p = event.payload || {}
      if (String(p.conversation_id) !== String(conversationId)) return

      switch (event.type) {
        case 'chat.message':
          if (p.message) setMessages((cur) => dedupeAppend(cur, p.message))
          break
        case 'chat.read':
          setDetails((cur) =>
            cur
              ? {
                  ...cur,
                  members: (cur.members || []).map((m) =>
                    m.user_id === p.user_id
                      ? { ...m, last_read_message_id: p.last_read_message_id }
                      : m
                  ),
                }
              : cur
          )
          break
        case 'chat.pinned':
        case 'chat.updated':
          // Состав/закрепы — редкие изменения: честная перезагрузка деталей.
          loadDetails().catch(() => {})
          break
        default:
      }
    },
    [conversationId, loadDetails]
  )

  const { isLive, status } = useEventStream(onEvent, { enabled })

  // Возврат в live после обрыва — перечитать: разрыв мог быть длиннее журнала.
  useEffect(() => {
    if (isLive && wasLiveRef.current === false && !loading) {
      reload()
    }
    wasLiveRef.current = isLive
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLive])

  // Опрос — только пока поток не работает.
  useEffect(() => {
    clearInterval(pollTimer.current)
    if (!enabled || isLive) return undefined
    pollTimer.current = setInterval(reload, POLL_MS)
    return () => clearInterval(pollTimer.current)
  }, [enabled, isLive, reload])

  /** Локально добавить своё сообщение (оптимистичный UI очереди). */
  const appendLocal = useCallback((message) => {
    setMessages((cur) => dedupeAppend(cur, message))
  }, [])

  const markRead = useCallback(
    async (messageId) => {
      try {
        await chatApi.markRead(conversationId, messageId)
      } catch {
        // Прочтение не критично — уйдёт со следующим прочтением.
      }
    },
    [conversationId]
  )

  return {
    messages,
    pinned,
    details,
    loading,
    error,
    hasMore,
    isLive,
    status,
    reload,
    loadOlder,
    appendLocal,
    markRead,
  }
}

export { dedupeAppend as _dedupeAppend }
